import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface Branch {
  id: string;
  name: string;
  code: string;
  city: string;
  region: string;
  contact_person: string;
  email: string;
  phone: string;
  address: string;
  is_active: boolean;
  monthly_target: number;
  created_at: string;
  updated_at: string;
}

export interface CreateBranchInput {
  name: string;
  code: string;
  city: string;
  region: string;
  contact_person: string;
  email: string;
  phone: string;
  address: string;
  monthly_target?: number;
  is_active?: boolean;
}

export const useBranches = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBranches = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('branches')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setBranches(data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Şubeler yüklenirken hata oluştu';
      setError(errorMessage);
      console.error('Error fetching branches:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createBranch = useCallback(async (branchData: CreateBranchInput) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: insertError } = await supabase
        .from('branches')
        .insert([{
          name: branchData.name,
          code: branchData.code,
          city: branchData.city,
          region: branchData.region,
          contact_person: branchData.contact_person,
          email: branchData.email,
          phone: branchData.phone,
          address: branchData.address,
          monthly_target: branchData.monthly_target || 0,
          is_active: branchData.is_active !== undefined ? branchData.is_active : true
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      await fetchBranches();

      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Şube oluşturulurken hata oluştu';
      setError(errorMessage);
      console.error('Error creating branch:', err);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [fetchBranches]);

  const updateBranch = useCallback(async (id: string, updates: Partial<CreateBranchInput>) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: updateError } = await supabase
        .from('branches')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      await fetchBranches();

      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Şube güncellenirken hata oluştu';
      setError(errorMessage);
      console.error('Error updating branch:', err);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [fetchBranches]);

  const deleteBranch = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from('branches')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      await fetchBranches();

      return { error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Şube silinirken hata oluştu';
      setError(errorMessage);
      console.error('Error deleting branch:', err);
      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [fetchBranches]);

  return {
    branches,
    loading,
    error,
    fetchBranches,
    createBranch,
    updateBranch,
    deleteBranch
  };
};
